import React, { useState, useEffect } from "react";
import Joyride, { CallBackProps, STATUS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import "../../Assets/scss/components/TourGuide.scss";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useSelector, useDispatch } from "react-redux";
import { updateUserProfileApi } from "../../Api/UserAction/UpdateUserDetails";
import { UPDATE_USER } from "../../Actions/Types";
import { allSteps } from "./tourSteps";

const TourGuide: React.FC = () => {
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [filteredSteps, setFilteredSteps] = useState<any[]>([]);
    const [isFromScripts, setIsFromScripts] = useState(false);
    const [isFromGettingStarted, setIsFromGettingStarted] = useState(false);
    const [isFromWidgetSettings, setIsFromWidgetSettings] = useState(false);
    const { currentWebsite, currentWebsitePlanStatus } = useSelector((store: StoreProptypes) => store);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((store: StoreProptypes) => store.user);
    const { accessToken } = useSelector((store: StoreProptypes) => store);

    useEffect(() => {
        document.body.style.overflow = run ? "hidden" : "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [run]);

    useEffect(() => {
        if (currentWebsite && currentWebsite?.is_eu_script_show_first === 1) {
            setRun(false);
        }
    }, [currentWebsite]);

    // ✅ Check DOM element
    const isTargetElementExists = (target: string): boolean => {
        if (target === "body") return true;
        try {
            return document.querySelector(target) !== null;
        } catch {
            return false;
        }
    };

    // ✅ FILTER STEPS AFTER DOM LOAD (IMPORTANT FIX)
    useEffect(() => {
        const timer = setTimeout(() => {
            const stepsData = allSteps.filter(step => {
                if (step.path !== location.pathname) return false;

                if (step.condition === "partner" && user?.department !== 4) return false;
                if (step.condition === "normal" && user?.department === 4) return false;

                // Check if overlays are shown and hide specific steps
                if (step.hideOnOverlay && currentWebsitePlanStatus) {
                    const hasOverlay =
                        currentWebsitePlanStatus.isFreeWidget ||
                        currentWebsitePlanStatus.isInit ||
                        currentWebsitePlanStatus.isExpired ||
                        currentWebsitePlanStatus.isCanceledPlan;

                    if (hasOverlay) return false;
                }

                // Hide step if coming from scripts page
                if (step.hideIfFromScripts && isFromScripts) {
                    return false;
                }

                // Show init-specific steps when isInit is true
                if (step.showOnInit && currentWebsitePlanStatus?.isInit) {
                    console.log('Checking init step:', step.target, isTargetElementExists(step.target as string));
                    return isTargetElementExists(step.target as string);
                }

                // Hide init-specific steps when isInit is false
                if (step.showOnInit && !currentWebsitePlanStatus?.isInit) {
                    return false;
                }

                // Hide regular steps when isInit is true 
                if (step.hideOnInit && currentWebsitePlanStatus?.isInit) {
                    return false;
                }

                return isTargetElementExists(step.target as string);
            });

            console.log('Filtered steps for tour:', stepsData.length, 'isInit:', currentWebsitePlanStatus?.isInit, 'isFromScripts:', isFromScripts);
            setFilteredSteps(stepsData);
        }, 1500); // Increased wait time for DOM

        return () => clearTimeout(timer);
    }, [location.pathname, user, currentWebsitePlanStatus, isFromScripts]);

    // ✅ START TOUR
    useEffect(() => {
        const normalUserPaths = [
            "/front",
            "/front/getting-started",
            "/front/scripts",
            "/front/widget-settings",
            //"/front/websites",
            "/front/manage-image-alt-text",
            "/front/manage-plan",
            "/front/orders",
            "/front/addon-services/modify-accessibility-menu", // Extra Large Site plan
        ];

        const partnerUserPaths = [
            "/front",
            "/front/getting-started",
            "/front/manage-plan",
            "/front/orders",
            "/front/widget-settings",
            "/front/scripts",
            "/front/domain-report",
            "/front/manage-image-alt-text",
            "/front/accessibility-checker",
            "/front/addon-services/modify-accessibility-menu", // Extra Large Site plan
        ];

        const tourPaths = user?.department === 4 ? partnerUserPaths : normalUserPaths;

        const shouldShowTour =
            tourPaths.includes(location.pathname) &&
            user?.is_tour_show === 0 &&
            // jub plan expired ya canceled ho jaye tour nhi show karna hai
            currentWebsitePlanStatus?.isExpired !== true &&
            // Extra Large Site plan check for modify accessibility menu
            (location.pathname !== "/front/addon-services/modify-accessibility-menu" ||
                currentWebsitePlanStatus?.planName === "Extra Large Site" || currentWebsitePlanStatus?.planName === "Gold" || currentWebsitePlanStatus?.planName === "Platinum");

        // Special case: widget-settings tour can run only when is_tour_show = 0
        const shouldShowWidgetSettingsTour =
            location.pathname === "/front/widget-settings" &&
            user?.is_tour_show === 0 &&
            currentWebsitePlanStatus?.isExpired !== true;

        if (!shouldShowTour && !shouldShowWidgetSettingsTour) return;

        const hasSeenTour = localStorage.getItem("is_tour_show");
        const tourStep = localStorage.getItem("tour_step");

        // start dashboard tour
        if ((location.pathname === "/front" || location.pathname === "/front/getting-started") && !hasSeenTour && !tourStep) {
            setRun(true);
            setStepIndex(0);
            localStorage.setItem("is_tour_show", String(user?.is_tour_show));
            if (location.pathname === "/front/getting-started") {
                setIsFromGettingStarted(true);
                localStorage.setItem("from_getting_started", "true");
            }
            return;
        }

        // start scripts page tour directly
        if (location.pathname === "/front/scripts" && !hasSeenTour && !tourStep) {
            setRun(true);
            setStepIndex(0);
            localStorage.setItem("is_tour_show", String(user?.is_tour_show));
            return;
        }

        // start widget-settings page tour directly
        if (location.pathname === "/front/widget-settings" && !hasSeenTour && !tourStep) {
            setRun(true);
            setStepIndex(0);
            localStorage.setItem("is_tour_show", String(user?.is_tour_show));
            setIsFromWidgetSettings(true);
            localStorage.setItem("from_widget_settings", "true");
            return;
        }


        // continue on whatever step was saved
        if (tourStep) {
            const expectedPath = tourStep === "front-dashboard" ? "/front" : `/front/${tourStep}`;
            if (location.pathname === expectedPath) {
                // Check if coming from scripts page
                const fromScripts = localStorage.getItem("from_scripts");
                if (fromScripts === "true") {
                    setIsFromScripts(true);
                    localStorage.removeItem("from_scripts");
                }

                // Check if coming from getting-started page
                const fromGettingStarted = localStorage.getItem("from_getting_started");
                if (fromGettingStarted === "true") {
                    setIsFromGettingStarted(true);
                    // Don't remove here, will be removed at the end of tour
                }

                // Check if coming from widget-settings page
                const fromWidgetSettings = localStorage.getItem("from_widget_settings");
                if (fromWidgetSettings === "true") {
                    setIsFromWidgetSettings(true);
                    // Don't remove here, will be removed at the end of tour
                }

                setRun(true);
                setStepIndex(0);
                localStorage.removeItem("tour_step");
            }
        }
    }, [location.pathname, user, currentWebsitePlanStatus]);

    // Update tour status in database
    const updateTourStatus = async () => {
        if (!user || !accessToken || user?.is_tour_show === 1) return;

        try {
            const response = await updateUserProfileApi(
                { ...user, is_tour_show: 1 },
                accessToken
            );

            if (response?.Data) {
                dispatch({
                    type: UPDATE_USER,
                    payload: { user: response.Data },
                });
            }
        } catch (err) {
            console.error("Failed to update tour status:", err);
        }
    };

    const handleCallback = async (data: CallBackProps) => {
        const { status, action, index, type } = data;

        // ✅ Step index control
        if (type === "step:after") {
            if (action === "next") {
                setStepIndex(index + 1);
            }
            if (action === "prev") setStepIndex(index - 1);
        }

        // ✅ Route mapping
        const normalRoutes: Record<string, string> = isFromGettingStarted ? {
            "/front/getting-started": "orders",
            "/front/orders": "front-dashboard",
            "/front/scripts": "",
            "/front": "widget-settings",
            "/front/widget-settings": "addon-services/modify-accessibility-menu",
        } : isFromWidgetSettings ? {
            "/front/widget-settings": "",
            "/front": "orders",
            "/front/orders": "addon-services/modify-accessibility-menu",
        } : {
            "/front/scripts": "",
            "/front": "widget-settings",
            "/front/widget-settings": "orders",
            "/front/orders": "addon-services/modify-accessibility-menu",
        };

        const partnerRoutes: Record<string, string> = isFromGettingStarted ? {
            "/front/getting-started": "orders",
            "/front/orders": "front-dashboard",
            "/front/scripts": "",
            "/front": "widget-settings",
            "/front/widget-settings": "domain-report",
            "/front/domain-report": "addon-services/modify-accessibility-menu",
        } : isFromWidgetSettings ? {
            "/front/widget-settings": "",
            "/front": "orders",
            "/front/orders": "domain-report",
            "/front/domain-report": "addon-services/modify-accessibility-menu",

        } : {
            "/front/scripts": "",
            "/front": "orders",
            "/front/orders": "widget-settings",
            "/front/widget-settings": "domain-report",
            "/front/domain-report": "addon-services/modify-accessibility-menu",
        };

        const nextRoutes = user?.department === 4 ? partnerRoutes : normalRoutes;
        const nextPage = nextRoutes[location.pathname];

        // Check if next page should be shown based on plan
        const shouldShowNextPage = nextPage &&
            (nextPage !== "addon-services/modify-accessibility-menu" ||
                currentWebsitePlanStatus?.planName === "Extra Large Site" ||
                currentWebsitePlanStatus?.planName === "Gold" ||
                currentWebsitePlanStatus?.planName === "Platinum");

        // ✅ 🔥 KEY LOGIC: HANDLE PAGE FLOW PROPERLY
        if (status === STATUS.FINISHED) {
            // ❌ DO NOT mark tour complete on getting-started
            if (location.pathname === "/front/getting-started") {
                setRun(false);
                localStorage.removeItem("tour_step");
                return; // 🚀 stop here
            }

            // 👉 If next page exists → continue tour
            if (shouldShowNextPage || nextPage === "" || nextPage === "front-dashboard") {
                setRun(false);

                let targetPath = "";
                if (nextPage === "front-dashboard" || nextPage === "") {
                    targetPath = "/front";
                } else {
                    targetPath = `/front/${nextPage}`;
                }

                localStorage.setItem("tour_step", nextPage === "front-dashboard" ? "front-dashboard" : (nextPage || "front-dashboard"));

                // Mark that user is coming from scripts page
                if (location.pathname === "/front/scripts") {
                    localStorage.setItem("from_scripts", "true");
                }

                // Preserve getting-started flag
                if (isFromGettingStarted) {
                    localStorage.setItem("from_getting_started", "true");
                }

                // Preserve widget-settings flag
                if (isFromWidgetSettings) {
                    localStorage.setItem("from_widget_settings", "true");
                }

                setTimeout(() => {
                    navigate(targetPath);
                }, 150);

                return;
            }

            // 👉 FINAL PAGE (no next route)
            setRun(false);
            localStorage.removeItem("tour_step");
            localStorage.removeItem("from_getting_started");
            localStorage.removeItem("from_widget_settings");
            localStorage.setItem("is_tour_show", "1");

            await updateTourStatus();
            return;
        }

        if (action === "close" || status === STATUS.SKIPPED) {
            // ❌ DO NOT mark tour complete on getting-started
            if (location.pathname === "/front/getting-started") {
                setRun(false);
                localStorage.removeItem("tour_step");
                return;
            }
            setRun(false);
            localStorage.removeItem("tour_step");
            localStorage.removeItem("from_getting_started");
            localStorage.removeItem("from_widget_settings");
            localStorage.setItem("is_tour_show", "1");
            await updateTourStatus();
        }
    };

    // ✅ Dynamic button text based on current page and step
    const getButtonText = () => {
        const nextRoutes: Record<string, string> = user?.department === 4 ? (isFromGettingStarted ? {
            "/front/getting-started": "orders",
            "/front/orders": "front-dashboard",
            "/front/scripts": "",
            "/front": "widget-settings",
            "/front/widget-settings": "domain-report",
            "/front/domain-report": "addon-services/modify-accessibility-menu",
        } : isFromWidgetSettings ? {
            "/front/widget-settings": "",
            "/front": "orders",
            "/front/orders": "domain-report",
            "/front/domain-report": "addon-services/modify-accessibility-menu",

        } : {
            "/front/scripts": "",
            "/front": "orders",
            "/front/orders": "widget-settings",
            "/front/widget-settings": "domain-report",
            "/front/domain-report": "addon-services/modify-accessibility-menu",
        }) : (isFromGettingStarted ? {
            "/front/getting-started": "orders",
            "/front/orders": "front-dashboard",
            "/front/scripts": "",
            "/front": "widget-settings",
            "/front/widget-settings": "addon-services/modify-accessibility-menu",
        } : isFromWidgetSettings ? {
            "/front/widget-settings": "",
            "/front": "orders",
            "/front/orders": "addon-services/modify-accessibility-menu",

        } : {
            "/front/scripts": "",
            "/front": "widget-settings",
            "/front/widget-settings": "orders",
            "/front/orders": "addon-services/modify-accessibility-menu",
        });

        const nextPage = nextRoutes[location.pathname];
        const shouldShowNextPage = nextPage !== undefined &&
            (nextPage !== "addon-services/modify-accessibility-menu" ||
                currentWebsitePlanStatus?.planName === "Extra Large Site" || currentWebsitePlanStatus?.planName === "Gold" || currentWebsitePlanStatus?.planName === "Platinum");
        const isLastStep = stepIndex === filteredSteps.length - 1;

        if (isLastStep && !shouldShowNextPage) {
            return "Finish";
        } else if (isLastStep && shouldShowNextPage) {
            return "Go to Next Module";
        }
        return "Next ▶";
    };
    return (
        <Joyride
            steps={filteredSteps}
            run={run}
            stepIndex={stepIndex}
            continuous
            showProgress
            showSkipButton
            spotlightClicks={false}
            disableScrollParentFix={false}
            // disableScrolling={false}
            // scrollToFirstStep={true}
            // scrollDuration={600}
            callback={handleCallback}
            scrollOffset={400}
            locale={{
                back: "◀ Back",
                close: "Got it!",
                last: getButtonText(),
                next: "Next ▶",
                skip: "Skip Tour",
            }}
            styles={{
                options: {
                    primaryColor: "#420083",
                    backgroundColor: "#fff",
                    textColor: "#333",
                    zIndex: 10000,
                },
            }}
        />
    );
};

export default TourGuide;
